import { type FC, useEffect, useMemo, useState } from "react"
import { Navigate, useParams } from "react-router"

import { type ChapterDripInterval, type Course, type CourseTag } from "@forento/core/models/course"
import { toPriceString } from "@forento/core/utilities/currency"
import { parseIntNumber, parseNumber } from "@forento/core/utilities/number"
import { useFile } from "@forento/ui/hooks/useFile"

import Layout, { PageBreadcrumb, PageHeader } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import ProductPricing from "~/components/ProductPricing"
import RichContentEditor from "~/components/RichContentEditor"
import { HashTabs } from "~/components/Tabs"
import ZapierId from "~/components/ZapierId"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import { useUser } from "~/contexts/UserContext"
import usePrice from "~/hooks/usePrice"
import {
	exportEditedRichContent,
	loadRichContent,
	parseRichContent,
	type RichContentValue,
} from "~/utilities/richContent"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

import ChapterDripping, { parseLocalDateTime, toLocalDateTimeString } from "./ChapterDripping"
import CourseInformation from "./CourseInformation"
import Exam from "./Exam"
import FreePreview from "./FreePreview"
import IssueCertificates from "./IssueCertificates"
import Prerequisite from "./Prerequisite"
import { Description, Title } from "./styles"
import Testers from "./Testers"

const CourseSettingsPage: FC = () => {
	const courseId = parseNumber(useParams().courseId) ?? -1

	const course = query.course.get.useQuery(courseId, { refetchOnWindowFocus: false })
	const tags = query.course.listTags.useQuery()

	if (course.data === null) return <Navigate to={routes.course.index()} />

	return (
		<Layout>
			<PageHeader>
				<PageBreadcrumb
					title={course.data?.title ?? "..."}
					path={[{ title: "Courses", link: routes.course.index() }]}
				/>
				{course.data && <ZapierId id={course.data.id} />}
			</PageHeader>
			{course.error || tags.error ? (
				<p>Failed to load course.</p>
			) : course.data === undefined || tags.data === undefined ? (
				<PartialLoadingPage />
			) : (
				<Content
					course={course.data}
					reloadCourse={course.refetch}
					tags={tags.data}
					reloadTags={tags.refetch}
				/>
			)}
		</Layout>
	)
}

type ContentProps = {
	course: Course
	reloadCourse(): Promise<unknown>
	tags: CourseTag[]
	reloadTags(): void
}
const Content: FC<ContentProps> = ({ course, reloadCourse, tags, reloadTags }) => {
	const alert = useAlert()
	const toast = useToast()
	const platform = useUser().user!.platform!

	const [title, setTitle] = useState(course.title)
	const [shortDescription, setShortDescription] = useState(course.shortDescription)
	const [commentsEnabled, setCommentsEnabled] = useState(course.commentsEnabled)
	const [autoApproveComments, setAutoApproveComments] = useState(course.autoApproveComments)
	const [selectedTags, setSelectedTags] = useState<number[]>(course.tags.map(tag => tag.id))
	const [chapterDripInterval, setChapterDripInterval] = useState<ChapterDripInterval | null>(
		course.chapterDripInterval,
	)
	const [chapterDripStartDate, setChapterDripStartDate] = useState<string | null>(
		course.chapterDripStartDate ? toLocalDateTimeString(course.chapterDripStartDate) : null,
	)
	const [issueCertificates, setIssueCertificates] = useState(course.issueCertificates)
	const [isFreeNavigation, setFreeNavigation] = useState(course.isFreeNavigation)
	const [chapterSkippingEnabled, setChapterSkippingEnabled] = useState(course.chapterSkippingEnabled)
	const { price, priceValue, setPrice } = usePrice(course.price?.amount ?? "")
	const [isManualOnly, setManualOnly] = useState(course.isManualOnly)
	const [accessTimeMonths, setAccessTimeMonths] = useState<string | null>(course.accessTimeMonths?.toString() ?? null)
	const [isBokaderoMemberRequired, setBokaderoMemberRequired] = useState(course.isBokaderoMemberRequired ?? false)
	const [membershipIds, setMembershipIds] = useState<number[]>(course.membershipIds)
	const [isPaused, setPaused] = useState(course.pauseDate !== null)
	const [freePreviewPageCount, setFreePreviewPageCount] = useState(course.freePreviewPageIds.length)
	const [prerequisiteCourseId, setPrerequisiteCourseId] = useState(course.prerequisiteCourseId)
	const [presentation, setPresentation] = useState<RichContentValue | null>(loadRichContent(course.presentation))

	const thumbnail = useFile(course.id, course.thumbnailFilePath ?? null)

	useEffect(() => {
		const isModified =
			title.trim() !== course.title.trim() ||
			shortDescription.trim() !== course.shortDescription.trim() ||
			commentsEnabled !== course.commentsEnabled ||
			autoApproveComments !== course.autoApproveComments ||
			selectedTags.toSorted().join(",") !==
				course.tags
					.map(tag => tag.id)
					.toSorted()
					.join(",") ||
			thumbnail.isModified ||
			chapterDripInterval !== course.chapterDripInterval ||
			(chapterDripStartDate ?? "") !==
				(course.chapterDripStartDate ? toLocalDateTimeString(course.chapterDripStartDate) : "") ||
			issueCertificates !== course.issueCertificates ||
			isFreeNavigation !== course.isFreeNavigation ||
			chapterSkippingEnabled !== course.chapterSkippingEnabled ||
			presentation?.isModified ||
			priceValue !== (course.price ? course.price.amount : null) ||
			isManualOnly !== course.isManualOnly ||
			parseIntNumber(accessTimeMonths) !== course.accessTimeMonths ||
			membershipIds.toSorted().join(",") !== course.membershipIds.toSorted().join(",") ||
			isPaused !== (course.pauseDate !== null) ||
			freePreviewPageCount !== course.freePreviewPageIds.length ||
			prerequisiteCourseId !== course.prerequisiteCourseId ||
			isBokaderoMemberRequired !== (course.isBokaderoMemberRequired ?? false)

		if (isModified) {
			toast.setUnsavedChanges(async () => {
				if (!course) return false
				if (priceValue !== null && priceValue < 1) {
					alert.show(
						"Error",
						`The price must be at least ${toPriceString({ amount: 1, currency: platform.currency })}.`,
					)
					return false
				}

				const accessTimeMonthsNumber = parseIntNumber(accessTimeMonths)
				if (accessTimeMonthsNumber !== null && accessTimeMonthsNumber < 1) {
					alert.show("Error", `The access time must be at least 1 month.`)
					return false
				}

				const chapterDripStartDateValue =
					chapterDripStartDate !== null ? parseLocalDateTime(chapterDripStartDate) : null
				if (chapterDripStartDate !== null && chapterDripStartDateValue === null) {
					alert.show("Error", "Choose a release date for the first chapter.")
					return false
				}
				try {
					const exportedPresentation = exportEditedRichContent(presentation)
					await trpc.course.update.mutate({
						id: course.id,
						data: {
							title,
							shortDescription,
							price: priceValue,
							isManualOnly,
							accessTimeMonths: accessTimeMonthsNumber,
							membershipIds,
							isPaused,
							freePreviewPageCount,
							prerequisiteCourseId,
							commentsEnabled,
							autoApproveComments,
							chapterDripInterval,
							chapterDripStartDate: chapterDripInterval !== null ? chapterDripStartDateValue : null,
							issueCertificates,
							isFreeNavigation,
							chapterSkippingEnabled,
							isBokaderoMemberRequired,
							presentation: exportedPresentation,
							tags: selectedTags,
							thumbnailDataUrl: await thumbnail.exportToDataUrl(),
						},
					})
					await reloadCourse()
					if (exportedPresentation !== undefined) {
						setPresentation(loadRichContent(exportedPresentation))
					}
					return true
				} catch (error) {
					console.error(error)
					await alert.show("Error", "Failed to save course.")
				}

				return false
			})
		} else {
			toast.clearUnsavedChanges()
		}
	}, [
		alert,
		autoApproveComments,
		chapterDripInterval,
		chapterDripStartDate,
		commentsEnabled,
		course,
		issueCertificates,
		isFreeNavigation,
		chapterSkippingEnabled,
		presentation,
		membershipIds,
		platform.currency,
		platform.paymentGateway?.isEnabled,
		priceValue,
		isManualOnly,
		accessTimeMonths,
		isPaused,
		freePreviewPageCount,
		prerequisiteCourseId,
		isBokaderoMemberRequired,
		reloadCourse,
		selectedTags,
		shortDescription,
		thumbnail,
		title,
		toast,
	])

	const initialPresentation = useMemo(() => parseRichContent(course.presentation), [course.presentation])

	return (
		<HashTabs
			tabs={[
				{
					title: "Course information",
					hash: "information",
					content: (
						<CourseInformation
							title={title}
							shortDescription={shortDescription}
							isFreeNavigation={isFreeNavigation}
							chapterSkippingEnabled={chapterSkippingEnabled}
							commentsEnabled={commentsEnabled}
							autoApproveComments={autoApproveComments}
							selectedTags={selectedTags}
							thumbnail={thumbnail}
							setTitle={setTitle}
							setShortDescription={setShortDescription}
							setFreeNavigation={setFreeNavigation}
							setChapterSkippingEnabled={setChapterSkippingEnabled}
							setCommentsEnabled={setCommentsEnabled}
							setAutoApproveComments={setAutoApproveComments}
							setSelectedTags={setSelectedTags}
							tags={tags}
							reloadTags={reloadTags}
						/>
					),
				},
				{
					title: "Exam",
					hash: "exam",
					content: <Exam courseId={course.id} />,
				},
				{
					title: "Issue certificates",
					hash: "certificates",
					content: (
						<IssueCertificates
							courseTitle={course.title}
							value={issueCertificates}
							onChange={setIssueCertificates}
						/>
					),
				},
				{
					title: "Chapter dripping",
					hash: "dripping",
					content: (
						<ChapterDripping
							interval={chapterDripInterval}
							onChange={setChapterDripInterval}
							startDate={chapterDripStartDate}
							onStartDateChange={setChapterDripStartDate}
						/>
					),
				},
				{
					title: "Course presentation",
					hash: "presentation",
					content: (
						<RichContentEditor
							className="mt-6"
							placeholder="Enter course presentation here..."
							initialValue={initialPresentation}
							setValue={setPresentation}
						/>
					),
				},
				{
					title: "Price & availability",
					hash: "pricing",
					content: (
						<>
							<Title>Course access</Title>
							<Description>Select how people access your course, and set a price.</Description>
							<ProductPricing
								type="course"
								price={price}
								setPrice={setPrice}
								isManualOnly={isManualOnly}
								setManualOnly={setManualOnly}
								accessTimeMonths={accessTimeMonths}
								setAccessTimeMonths={setAccessTimeMonths}
								isBokaderoMemberRequired={isBokaderoMemberRequired}
								setBokaderoMemberRequired={setBokaderoMemberRequired}
								membershipIds={membershipIds}
								setMembershipIds={setMembershipIds}
								isPaused={isPaused}
								setPaused={setPaused}
							/>
							{(priceValue !== null || membershipIds.length > 0 || isManualOnly) && (
								<FreePreview
									chapters={course.chapters}
									pageCount={freePreviewPageCount}
									onChange={setFreePreviewPageCount}
								/>
							)}
							<Prerequisite
								courseId={course.id}
								prerequisiteCourseId={prerequisiteCourseId}
								onChange={setPrerequisiteCourseId}
							/>
						</>
					),
				},
				{
					title: "Testers",
					hash: "testers",
					content: <Testers courseId={course.id} />,
				},
			]}
		/>
	)
}

export default CourseSettingsPage
